import { useEffect, useState } from "react"
import { srcsetFor, sizedUrl } from "aldoo-ra/ImageVariants"

// Catalog images arrive as the plain url of the original; the media mount
// answers `?size=<width>` with a variant (aldoo-na/image-variants), so the
// srcset is built here over those buckets. CMS promotion images still arrive
// as { default: [webpSrcset, jpegSrcset] } ("url 1024w 1024h, url 512w …").
const FIRST_URL = /^\S*/

// the catalog media library, local (`/static/book_media_library/`) or CDN
const isCatalogMedia = (url) => typeof url === "string" && url.includes("/book_media_library/")

// the variants are asked for as WebP: a quarter or more smaller than the
// jpeg/png originals (which stay as they are), and every browser decodes it
const CATALOG_FORMAT = "webp"

/** srcset for a catalog image url ("" for anything the resizer does not serve). */
export const catalogSrcSet = (url) => (isCatalogMedia(url) ? srcsetFor(url, undefined, CATALOG_FORMAT) : "")

/** One sized variant of a catalog image url (others come back unchanged). */
export const catalogSizedUrl = (url, width) => (isCatalogMedia(url) ? sizedUrl(url, width, CATALOG_FORMAT) : url)

export const IMG_TYPES = ["image/webp", "image/jpeg"]

// Strip the non-standard height descriptor: "1024w 1024h" -> "1024w"
export const cleanSrcSet = (srcset) => srcset?.replace(/(\d+w)\s+\d+h/g, "$1")

// First (largest) URL from the first (webp) srcset — used for og:image, backgrounds
export const getImageURL = (item) => {
  if (!item) return ""
  if (typeof item === "string") return item
  const list = item.default
  if (!list?.length) return ""
  return list[0].split(",")[0].trim().split(" ")[0]
}

// Fallback <img src>: first URL of the last (jpeg) srcset
export const getFallbackSrc = (item) => {
  if (!item) return ""
  if (typeof item === "string") return item
  const list = item.default
  if (!list?.length) return ""
  return FIRST_URL.exec(list[list.length - 1])?.[0] || ""
}

// Natural sizes of plain image urls, learned by loading them (useImageSize)
const naturalSizes = new Map()

// Natural size of an image: the "...--1920x1080.webp" of the old responsive
// urls, or what useImageSize measured for a plain url ({0, 0} until then)
export function getImageSize(img) {
  if (!img) return { width: 0, height: 0 }
  if (typeof img === "string") return naturalSizes.get(img) || { width: 0, height: 0 }
  const parsed = /--(\d+)x(\d+)\./m.exec(img.default?.[0] || "")
  if (!parsed) return { width: 0, height: 0 }
  return { width: Number(parsed[1]), height: Number(parsed[2]) }
}

/**
 * getImageSize that also works for plain urls: the image is loaded once in the
 * background (the browser cache serves the <img> that shows it) and the
 * component re-renders with its natural size.
 */
export function useImageSize(img) {
  const [size, setSize] = useState(() => getImageSize(img))
  const url = typeof img === "string" ? img : ""
  useEffect(() => {
    const known = getImageSize(img)
    setSize(known)
    if (known.width || !url || typeof Image === "undefined") return
    let cancelled = false
    const probe = new Image()
    probe.onload = () => {
      const measured = { width: probe.naturalWidth, height: probe.naturalHeight }
      naturalSizes.set(url, measured)
      if (!cancelled) setSize(measured)
    }
    probe.src = url
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, typeof img === "string" ? "" : img?.default?.[0]])
  return size
}

export const hasImage = (item) =>
  !!(item && (typeof item === "string" || item.default?.length))
